import React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import AssignmentActionFlowModal, {
	ActionTarget,
	ActionTargetOption,
	ActionSummary
} from '../AssignmentActionFlowModal';
import { pluralLabel } from 'LearningManager/helpers/role';

export type UnassignTarget = ActionTarget;

const getUnassignOptions = (t: TFunction): ActionTargetOption[] => [
	{
		value: 'all',
		title: t('learning.unassignScheduledModal.options.all.title'),
		description: t('learning.unassignScheduledModal.options.all.description')
	},
	{
		value: 'specific',
		title: t('learning.unassignScheduledModal.options.specific.title'),
		description: t('learning.unassignScheduledModal.options.specific.description')
	}
];

const unassignAllItems = (
	{ userCount }: ActionSummary,
	t: TFunction
): string[] => [
	t(pluralLabel('learning.unassignScheduledModal.confirmAll.items.learners', userCount), { count: userCount }),
	t('learning.unassignScheduledModal.confirmAll.items.deleteProgress')
];

const unassignSpecificItems = (
	summary: ActionSummary,
	t: TFunction
): string[] => [
	...(summary.entityCount
		? [
				t(pluralLabel('learning.unassignScheduledModal.confirmSpecific.items.selections', summary.entityCount), {
					count: summary.entityCount
				})
			]
		: []),
	...unassignAllItems(summary, t)
];

interface UnassignScheduledModalProps {
	visible: boolean;
	assignmentId: number;
	scopes: string[];
	tenantId?: number;
	onCancel: () => void;
	onContinue: (
		target: UnassignTarget,
		userIds?: string[]
	) => void | Promise<void>;
}

const UnassignScheduledModal: React.FC<UnassignScheduledModalProps> = ({
	visible,
	assignmentId,
	scopes,
	tenantId,
	onCancel,
	onContinue
}) => {
	const { t } = useTranslation();

	return (
		<AssignmentActionFlowModal
			visible={visible}
			assignmentId={assignmentId}
			scopes={scopes}
			tenantId={tenantId}
			audience
			onCancel={onCancel}
			onContinue={onContinue}
			select={{
				title: t('learning.unassignScheduledModal.select.title'),
				description: t('learning.unassignScheduledModal.select.description'),
				options: getUnassignOptions(t),
				actionLabel: t('learning.unassignScheduledModal.actions.continue')
			}}
			confirmAll={{
				title: t('learning.unassignScheduledModal.confirmAll.title'),
				intro: t('learning.unassignScheduledModal.confirmAll.intro'),
				items: (summary) => unassignAllItems(summary, t),
				actionLabel: t('learning.unassignScheduledModal.actions.unassign')
			}}
			specific={{
				title: t('learning.unassignScheduledModal.specific.title'),
				description: t('learning.unassignScheduledModal.specific.description'),
				actionLabel: t('learning.unassignScheduledModal.actions.continue')
			}}
			confirmSpecific={{
				title: t('learning.unassignScheduledModal.confirmSpecific.title'),
				intro: t('learning.unassignScheduledModal.confirmSpecific.intro'),
				items: (summary) => unassignSpecificItems(summary, t),
				actionLabel: t('learning.unassignScheduledModal.actions.unassign')
			}}
		/>
	);
};

export default UnassignScheduledModal;